import zod from "zod";
import { offerDocumentSchema } from "./documentsSchemas";
import { funderSchema } from "@/api/http/schemas/funderSchemas";

const dateSchema = zod.string().regex(/^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);

export enum ProductId {
  Arloc = 1,
  LineOfCredit = 2,
  ReceivablesPurchase = 3,
  TermLoan = 4,
  EquipmentFinancing = 5,
  Erc = 9,
}

export const offerProductIdSchema = zod.nativeEnum(ProductId);

export enum PaymentPeriod {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  BiWeekly = 4,
}

const paymentPeriodSchema = zod.nativeEnum(PaymentPeriod);

export enum OfferStatus {
  Accepted = 1,
  Sent = 2,
  Declined = 3,
  Retracted = 4,
}

const offerStatusSchema = zod.nativeEnum(OfferStatus);

export type PaymentPeriodId = zod.infer<typeof paymentPeriodSchema>;

export const offerDataSchema = zod.object({
  base_rate: zod.number().optional().nullable(),
  erc_credit_amount: zod.number().optional().nullable(),
  erc_filing_date: dateSchema.optional().nullable(),
  funding_amount: zod.number().optional().nullable(),
  initial_draw_fee: zod.number().optional().nullable(),
  interest_charged: zod.number().optional().nullable(),
  max_facility_amount: zod.number().optional().nullable(),
  max_line_of_credit: zod.number().optional().nullable(),
  max_sell_factor_rate: zod.number().optional().nullable(),
  max_sell_interest_rate: zod.number().optional().nullable(),
  max_term: zod.number().optional().nullable(),
  monthly_interest: zod.number().optional().nullable(),
  number_of_payments: zod.number().nullish(),
  offer_amount: zod.number().optional().nullable(),
  offer_expiration_date: dateSchema.optional().nullable(),
  offer_generated_date: dateSchema.optional().nullable(),
  payback_amount: zod.number().optional().nullable(),
  payment_amount: zod.number().optional().nullable(),
  payment_frequency: paymentPeriodSchema.nullish(),
  term: zod.number().optional().nullable(),
  term_length: zod.number().optional().nullable(),
  term_length_timeframe: zod.number().optional().nullable(),
  total_financed_amount: zod.number().optional().nullable(),
});

export type OfferData = zod.infer<typeof offerDataSchema>;

export const offerDetailsTypeSchema = zod.union([
  zod.literal("currency"),
  zod.literal("date"),
  zod.literal("float"),
  zod.literal("integer"),
  zod.literal("options"),
  zod.literal("percentage"),
  zod.literal("string"),
  zod.literal("text"),
  zod.literal("url"),
]);

export type OfferDetailsType = zod.infer<typeof offerDetailsTypeSchema>;

export const offerDetailsSectionSchema = zod.union([
  zod.literal("primary"),
  zod.literal("expanded"),
]);

export type OfferDetailsSection = zod.infer<typeof offerDetailsSectionSchema>;

export const offerDetailsSchema = zod.object({
  label: zod.string(),
  name: zod.string(),
  section: offerDetailsSectionSchema,
  options: zod.union([zod.array(zod.string()), zod.record(zod.string(), zod.string())]).optional(),
  type: offerDetailsTypeSchema,
  value: zod.union([zod.string(), zod.number()]).optional().nullable(),
  weight: zod.number(),
  dynamic: zod.boolean(),
});

export const OfferTemplateSchema = zod.object({
  id: zod.string(),
  name: zod.string(),
  description: zod.string().nullable(),
  is_fallback: zod.boolean(),
  created_at: zod.string().datetime(),
  updated_at: zod.string().datetime(),
});

export type OfferDetails = zod.infer<typeof offerDetailsSchema>;

export const offerSchema = zod.object({
  closing_details: zod.array(offerDetailsSchema),
  created_at: zod.string().datetime(),
  declined_reason: zod.string().optional().nullable(),
  funder: funderSchema.optional(),
  has_closing_details: zod.boolean(),
  has_checkout_snapshot: zod.boolean(),
  id: zod.string(),
  offer_details: zod.array(offerDetailsSchema),
  product_id: offerProductIdSchema,
  required_stips: zod.array(offerDocumentSchema),
  status: offerStatusSchema.nullable(),
  funder_id: zod.string(),
  offer_template_id: zod.string(),
  template: OfferTemplateSchema,
});

export type Offer = zod.infer<typeof offerSchema>;

const groupedOfferSchema = zod.object({
  id: zod.string(),
  funder_id: zod.string(),
  offer_template_id: zod.string(),
  weight: zod.number(),
  offers: zod.array(offerSchema),
});

export type GroupedOffer = zod.infer<typeof groupedOfferSchema>;

export const updatedOfferSchema = offerSchema.omit({ required_stips: true });

export type UpdatedOffer = zod.infer<typeof updatedOfferSchema>;

export const offerDynamicDetailsSchema = zod.object({
  name: offerDetailsSchema.shape.name,
  values: zod.array(offerDetailsSchema.shape.value),
  type: offerDetailsTypeSchema,
  items: zod.array(offerDetailsSchema.extend({ offer_id: offerSchema.shape.id })),
  options: offerDetailsSchema.shape.options,
  label: offerDetailsSchema.shape.label,
  weight: offerDetailsSchema.shape.weight,
  offers: zod.record(offerSchema.shape.id, offerDetailsSchema.shape.value),
});

export type OfferDynamicDetails = zod.infer<typeof offerDynamicDetailsSchema>;
